import { createHmac, timingSafeEqual } from "node:crypto"

import { Types } from "mongoose"
import type { PaginationSpec } from "@rpcbase/api"

import { PaginationValidationError } from "./errors"
import { normalizePaginationSpec } from "./normalizeSpec"


type CursorValue =
  | null
  | string
  | number
  | boolean
  | { $date: string }
  | { $oid: string }

type CursorPayloadV1 = {
  v: 1;
  values: Record<string, CursorValue>;
}

export type PaginationCursorCodecOptions = {
  signingSecret: string | Buffer;
  maxLimit?: number;
}

export const encodePaginationCursor = (
  spec: PaginationSpec,
  node: unknown,
  options: PaginationCursorCodecOptions,
): string => {
  const normalized = normalizePaginationSpec(spec, { maxLimit: options.maxLimit })

  const values = Object.create(null) as Record<string, CursorValue>
  for (const { field } of normalized.sort) {
    const value = readFieldValue(node, field)
    if (typeof value === "undefined") {
      throw new Error(`Pagination cursor encode failed (missing field: ${field})`)
    }
    values[field] = encodeCursorValue(field, value)
  }

  const payload: CursorPayloadV1 = { v: 1, values }
  const payloadB64 = Buffer.from(JSON.stringify(payload), "utf8").toString("base64url")

  const sigB64 = signCursorPayloadB64(payloadB64, options.signingSecret)
  return `${payloadB64}.${sigB64}`
}

export const decodePaginationCursor = (
  spec: PaginationSpec,
  cursor: string,
  options: PaginationCursorCodecOptions,
): Record<string, unknown> => {
  const normalized = normalizePaginationSpec(spec, { maxLimit: options.maxLimit })

  const [payloadB64, sigB64, ...rest] = cursor.split(".")
  if (rest.length > 0) throw new PaginationValidationError("Invalid pagination cursor format")
  if (!sigB64) throw new PaginationValidationError("Invalid pagination cursor format")
  verifyCursorSignature(payloadB64, sigB64, options.signingSecret)

  const payloadRaw = Buffer.from(payloadB64, "base64url").toString("utf8")

  let payloadUnknown: unknown
  try {
    payloadUnknown = JSON.parse(payloadRaw)
  } catch {
    throw new PaginationValidationError("Invalid pagination cursor payload")
  }

  if (!payloadUnknown || typeof payloadUnknown !== "object") throw new PaginationValidationError("Invalid pagination cursor payload")
  const payload = payloadUnknown as Partial<CursorPayloadV1>

  if (payload.v !== 1) throw new PaginationValidationError("Unsupported pagination cursor version")
  if (!payload.values || typeof payload.values !== "object") throw new PaginationValidationError("Invalid pagination cursor payload")

  const decoded = Object.create(null) as Record<string, unknown>
  for (const { field } of normalized.sort) {
    if (!Object.prototype.hasOwnProperty.call(payload.values, field)) {
      throw new PaginationValidationError(`Pagination cursor missing field: ${field}`)
    }
    const encodedValue = (payload.values as Record<string, CursorValue>)[field]
    decoded[field] = decodeCursorValue(field, encodedValue)
  }

  return decoded
}

const signCursorPayloadB64 = (payloadB64: string, secret: string | Buffer): string => {
  return createHmac("sha256", secret).update(payloadB64, "utf8").digest("base64url")
}

const verifyCursorSignature = (payloadB64: string, sigB64: string, secret: string | Buffer): void => {
  const expectedSigB64 = signCursorPayloadB64(payloadB64, secret)
  const a = Buffer.from(sigB64, "utf8")
  const b = Buffer.from(expectedSigB64, "utf8")
  if (a.length !== b.length || !timingSafeEqual(a, b)) {
    throw new PaginationValidationError("Invalid pagination cursor signature")
  }
}

const unwrapCursorComparableValue = (value: unknown): unknown => {
  if (value === null || typeof value !== "object") return value
  if (value instanceof Date || value instanceof Types.ObjectId) return value

  let current: unknown = value
  for (let depth = 0; depth < 4; depth += 1) {
    if (!current || typeof current !== "object") return current
    if (current instanceof Date || current instanceof Types.ObjectId) return current
    if (!("_id" in current)) return current
    current = (current as { _id: unknown })._id
  }

  return current
}

const encodeCursorValue = (field: string, value: unknown): CursorValue => {
  const comparableValue = unwrapCursorComparableValue(value)

  if (comparableValue === null) return null

  if (field === "_id") {
    if (comparableValue instanceof Types.ObjectId) return { $oid: comparableValue.toHexString() }
    if (typeof comparableValue === "string") return comparableValue
    throw new Error("Pagination cursor encode failed (_id must be an ObjectId or string)")
  }

  if (comparableValue instanceof Date) return { $date: comparableValue.toISOString() }

  if (
    typeof comparableValue === "string" ||
    typeof comparableValue === "number" ||
    typeof comparableValue === "boolean"
  ) {
    return comparableValue
  }

  if (comparableValue instanceof Types.ObjectId) return { $oid: comparableValue.toHexString() }

  throw new Error(`Unsupported pagination cursor value type for field: ${field}`)
}

const decodeCursorValue = (field: string, value: CursorValue): unknown => {
  if (value === null) return null

  if (typeof value === "string") {
    return value
  }

  if (typeof value === "number" || typeof value === "boolean") return value

  if (!value || typeof value !== "object") throw new PaginationValidationError(`Invalid pagination cursor value for field: ${field}`)

  if ("$date" in value) {
    if (typeof value.$date !== "string") throw new PaginationValidationError(`Invalid pagination cursor date for field: ${field}`)
    const d = new Date(value.$date)
    if (Number.isNaN(d.getTime())) throw new PaginationValidationError(`Invalid pagination cursor date for field: ${field}`)
    return d
  }

  if ("$oid" in value) {
    if (typeof value.$oid !== "string" || !Types.ObjectId.isValid(value.$oid)) {
      throw new PaginationValidationError(`Invalid pagination cursor ObjectId for field: ${field}`)
    }
    return new Types.ObjectId(value.$oid)
  }

  throw new PaginationValidationError(`Invalid pagination cursor value for field: ${field}`)
}

const readFieldValue = (node: unknown, field: string): unknown => {
  if (!node || typeof node !== "object") return undefined

  if ("get" in node && typeof (node as { get?: unknown }).get === "function") {
    return (node as { get: (path: string) => unknown }).get(field)
  }

  return field.split(".").reduce((acc, key) => {
    if (!acc || typeof acc !== "object") return undefined
    return (acc as Record<string, unknown>)[key]
  }, node as unknown)
}
